import { getCountryOptions } from '~/types/Address';
import { type CarrierFormField } from '~/types/CarrierFormField';

import { type CarrierFormType } from '../CarrierForm/CarrierForm';

const upsEditParameters: CarrierFormField[] = [
  {
    disabled: true,
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'account_number',
    label: 'UPS Account Number',
    maxLength: 6,
    minLength: null,
    placeholderText: null,
    rank: 5,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: false,
    key: 'cost_center',
    label: 'Mail Innovations Cost Center',
    maxLength: 20, // arbitrary number, online examples seem to be length of 8
    minLength: null,
    placeholderText: null,
    rank: 10,
    tooltip: 'An identifier that UPS Mail Innovations assigned to your account.',
  },
  {
    displayType: 'show',
    fieldType: 'choice',
    helpUrl: null,
    hiddenByKey: 'cost_center',
    hiddenByValue: undefined,
    isRequired: false,
    key: 'usps_endorsement',
    label: 'USPS Endorsement',
    maxLength: null,
    minLength: null,
    options: [
      { label: 'Return Service Requested (1)', value: 1 },
      { label: 'Forwarding Service Requested (2)', value: 2 },
      { label: 'Address Service Requested (3)', value: 3 },
      { label: 'Change Service Requested (4)', value: 4 },
      { label: 'No Service Selected (5)', value: 5 },
    ],
    placeholderText: null,
    rank: 15,
    tooltip:
      'Specify how USPS, the last-mile carrier for UPS Mail Innovations, should handle non-deliverable packages.',
  },
  {
    displayType: 'show',
    fieldType: 'boolean',
    helpUrl: null,
    isRequired: false,
    key: 'surepost',
    label: 'Activate UPS Surepost',
    maxLength: null,
    minLength: null,
    placeholderText: null,
    rank: 20,
    tooltip:
      'Activate this field if your account supports UPS Surepost. This needs to be explicitly enabled by UPS.',
  },
];

const canadaPostCreateParameters: CarrierFormField[] = [
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'street_number',
    label: 'Street #',
    maxLength: 10,
    minLength: null,
    placeholderText: null,
    rank: 10,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'street',
    label: 'Street',
    maxLength: 60,
    minLength: null,
    placeholderText: null,
    rank: 20,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: false,
    key: 'unit_suite_apt',
    label: 'Unit',
    maxLength: 10,
    minLength: null,
    placeholderText: null,
    rank: 30,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'city',
    label: 'City',
    maxLength: 40,
    minLength: null,
    placeholderText: null,
    rank: 40,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'prov_state',
    label: 'State / Province',
    maxLength: 2,
    minLength: null,
    placeholderText: 'Enter a 2-letter state code',
    rank: 50,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'postal_zip_code',
    label: 'Zip / Postal Code',
    maxLength: 10,
    minLength: null,
    placeholderText: null,
    rank: 60,
  },
  {
    displayType: 'show',
    fieldType: 'choice',
    helpUrl: null,
    isRequired: true,
    key: 'country_code',
    label: 'Country',
    maxLength: null,
    minLength: null,
    options: [
      { label: 'Canada', value: 'CA' },
      { label: 'United States', value: 'US' },
    ],
    placeholderText: null,
    rank: 70,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'phone_number',
    label: 'Phone',
    maxLength: 30,
    minLength: null,
    placeholderText: null,
    rank: 80,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'legal_name',
    label: 'Business Name',
    maxLength: 40,
    minLength: null,
    placeholderText: null,
    rank: 90,
  },
  {
    displayType: 'show',
    fieldType: 'integer',
    helpUrl: null,
    isRequired: false,
    key: 'canada_parcel_volume',
    label: 'How many items do you ship monthly to Canada?',
    maxLength: 7,
    minLength: null,
    placeholderText: null,
    rank: 100,
  },
  {
    displayType: 'show',
    fieldType: 'integer',
    helpUrl: null,
    isRequired: false,
    key: 'us_parcel_volume',
    label: 'How many items do you ship monthly to United States?',
    maxLength: 7,
    minLength: null,
    placeholderText: null,
    rank: 110,
  },
  {
    displayType: 'show',
    fieldType: 'integer',
    helpUrl: null,
    isRequired: false,
    key: 'international_parcel_volume',
    label: 'How many items do you ship monthly to international countries?',
    maxLength: 7,
    minLength: null,
    placeholderText: null,
    rank: 120,
  },
  {
    defaultValue: true,
    displayType: 'show',
    fieldType: 'boolean',
    helpUrl: null,
    isRequired: true,
    key: 'canada_post_agreement',
    label:
      'By checking this box, you agree to the <a href="https://goshippo.com/partners_static/canada_post/terms/">Canada Post Shipping Service Agreement</a>',
    maxLength: null,
    minLength: null,
    placeholderText: null,
    rank: 150,
  },
];

export const eBayCanadaPostConnectParameters: CarrierFormField[] = [
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'customer_number',
    label: 'Canada Post Customer Number',
    maxLength: 10,
    minLength: null,
    placeholderText: null,
    rank: 20,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'postal_zip_code',
    label: 'Zip / Postal Code',
    maxLength: 10,
    minLength: null,
    placeholderText: null,
    rank: 40,
  },
];

const canadaPostParameters: CarrierFormField[] = [
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'customer_number',
    label: 'Canada Post Customer Number',
    maxLength: 10,
    minLength: null,
    placeholderText: null,
    rank: 20,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl:
      'https://support.goshippo.com/hc/en-us/articles/201772755-Where-do-I-get-my-Canada-Post-API-username-and-password-',
    isRequired: true,
    key: 'account_id',
    label: 'API Username',
    maxLength: 16,
    minLength: null,
    placeholderText: null,
    rank: 40,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'api_password',
    label: 'API Password',
    maxLength: 22,
    minLength: null,
    placeholderText: null,
    rank: 60,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: false,
    key: 'contract_id',
    label: 'Contract ID (optional)',
    maxLength: 10,
    minLength: null,
    placeholderText: null,
    rank: 80,
  },
  {
    defaultValue: 'CreditCard',
    displayType: 'show',
    fieldType: 'choice',
    helpUrl: null,
    isRequired: true,
    key: 'payment_method',
    label: 'Payment Method',
    maxLength: null,
    minLength: null,
    options: [
      { label: 'Credit Card', value: 'CreditCard' },
      { label: 'Bill Account', value: 'Account' },
    ],
    placeholderText: null,
    rank: 100,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: false,
    key: 'zonos_account_key',
    label: 'Zonos Account Key',
    maxLength: 255,
    minLength: null,
    placeholderText: null,
    rank: 110,
  },
  {
    displayType: 'show',
    fieldType: 'boolean',
    helpUrl: null,
    isRequired: false,
    key: 'use_manifests',
    label: 'Use Manifests',
    maxLength: null,
    minLength: null,
    placeholderText: null,
    rank: 120,
  },
];

const lasershipParameters: CarrierFormField[] = [
  {
    displayType: 'mask',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'account_id',
    label: 'Lasership API Key',
    maxLength: 255,
    minLength: null,
    placeholderText: null,
    rank: 20,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'lasership_apiid',
    label: 'API ID',
    maxLength: 255,
    minLength: null,
    placeholderText: null,
    rank: 40,
  },
  {
    displayType: 'show',
    fieldType: 'military_time',
    helpUrl: null,
    isRequired: true,
    key: 'critical_pull_time',
    label: 'Critical Pull Time',
    maxLength: null,
    minLength: null,
    placeholderText: 'HH:MM 24-hour time format',
    rank: 60,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: false,
    key: 'default_customer_branch',
    label: 'Default Customer Branch',
    maxLength: 255,
    minLength: null,
    placeholderText: null,
    rank: 80,
  },
];

const dhlExpressParameters: CarrierFormField[] = [
  {
    displayType: 'show_partial',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'account_id',
    label: 'DHL Express Account Number',
    maxLength: 10,
    minLength: null,
    placeholderText: null,
    rank: 20,
  },
  {
    displayType: 'show_partial',
    fieldType: 'string',
    helpUrl: 'https://support.goshippo.com/hc/en-us/articles/4409158652571',
    isRequired: true,
    key: 'site_id',
    label: 'Site ID',
    maxLength: 32,
    minLength: 6,
    placeholderText: null,
    rank: 30,
  },
  {
    displayType: 'mask',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'password',
    label: 'Password',
    maxLength: 20,
    minLength: 8,
    placeholderText: null,
    rank: 40,
  },
  {
    displayType: 'show',
    fieldType: 'choice',
    helpUrl: null,
    isRequired: true,
    key: 'payment_country',
    label: 'Registered Country',
    maxLength: null,
    minLength: null,
    options: getCountryOptions(),
    placeholderText: null,
    rank: 60,
  },
];

export const AUSPOST_MYPOST_OPTIONS = [
  {
    label: 'MyPost (under 1,000 labels per year)',
    value: 'is_mypost',
  },
  {
    label: 'eParcel (1,000+ labels a year)',
    value: 'is_eparcel',
  },
];

const UPS_REGISTRATION_COUNTRIES = [
  {
    label: 'Argentina',
    value: 'AR',
  },
  {
    label: 'Australia',
    value: 'AU',
  },
  {
    label: 'Austria',
    value: 'AT',
  },
  {
    label: 'Belgium',
    value: 'BE',
  },
  {
    label: 'Brazil',
    value: 'BR',
  },
  {
    label: 'Canada',
    value: 'CA',
  },
  {
    label: 'Chile',
    value: 'CL',
  },
  {
    label: 'China',
    value: 'CN',
  },
  {
    label: 'Costa Rica',
    value: 'CR',
  },
  {
    label: 'Czech Republic',
    value: 'CZ',
  },
  {
    label: 'Denmark',
    value: 'DK',
  },
  {
    label: 'Dominican Republic',
    value: 'DO',
  },
  {
    label: 'Ecuador',
    value: 'EC',
  },
  {
    label: 'Finland',
    value: 'FI',
  },
  {
    label: 'France',
    value: 'FR',
  },
  {
    label: 'Germany',
    value: 'DE',
  },
  {
    label: 'Greece',
    value: 'GR',
  },
  {
    label: 'Guatemala',
    value: 'GT',
  },
  {
    label: 'Guernsey',
    value: 'GG',
  },
  {
    label: 'Hong Kong',
    value: 'HK',
  },
  {
    label: 'Hungary',
    value: 'HU',
  },
  {
    label: 'India',
    value: 'IN',
  },
  {
    label: 'Indonesia',
    value: 'ID',
  },
  {
    label: 'Ireland',
    value: 'IE',
  },
  {
    label: 'Israel',
    value: 'IL',
  },
  {
    label: 'Italy',
    value: 'IT',
  },
  {
    label: 'Japan',
    value: 'JP',
  },
  {
    label: 'Jersey',
    value: 'JE',
  },
  {
    label: 'Korea, Republic of',
    value: 'KR',
  },
  {
    label: 'Lithuania',
    value: 'LT',
  },
  {
    label: 'Macao',
    value: 'MO',
  },
  {
    label: 'Malaysia',
    value: 'MY',
  },
  {
    label: 'Mexico',
    value: 'MX',
  },
  {
    label: 'Netherlands',
    value: 'NL',
  },
  {
    label: 'New Zealand',
    value: 'NZ',
  },
  {
    label: 'Norway',
    value: 'NO',
  },
  {
    label: 'Panama',
    value: 'PA',
  },
  {
    label: 'Philippines',
    value: 'PH',
  },
  {
    label: 'Poland',
    value: 'PL',
  },
  {
    label: 'Portugal',
    value: 'PT',
  },
  {
    label: 'Puerto Rico',
    value: 'PR',
  },
  {
    label: 'Romania',
    value: 'RO',
  },
  {
    label: 'Russian Federation',
    value: 'RU',
  },
  {
    label: 'Singapore',
    value: 'SG',
  },
  {
    label: 'Spain',
    value: 'ES',
  },
  {
    label: 'Sweden',
    value: 'SE',
  },
  {
    label: 'Switzerland',
    value: 'CH',
  },
  {
    label: 'Taiwan, Province of China',
    value: 'TW',
  },
  {
    label: 'Thailand',
    value: 'TH',
  },
  {
    label: 'Turkey',
    value: 'TR',
  },
  {
    label: 'United Arab Emirates',
    value: 'AE',
  },
  {
    label: 'United Kingdom',
    value: 'GB',
  },
  {
    label: 'United States',
    value: 'US',
  },
  {
    label: 'Viet Nam',
    value: 'VN',
  },
];

const upsConnectParametersOauth: CarrierFormField[] = [
  {
    displayType: 'show',
    fieldType: 'choice',
    helpUrl: null,
    isRequired: true,
    key: 'billing_address_country_iso2',
    label: 'Country',
    maxLength: null,
    minLength: null,
    options: UPS_REGISTRATION_COUNTRIES,
    placeholderText: null,
    rank: 65,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'account_number',
    label: 'UPS Account Number',
    maxLength: 20,
    minLength: null,
    placeholderText: null,
    rank: 70,
  },
  {
    displayType: 'show',
    fieldType: 'boolean',
    helpUrl: null,
    isRequired: true,
    key: 'ups_agreements',
    label:
      'By checking this box, you agree to the <a style="color: #226015" rel="noreferrer noopener" target="_blank" href="https://assets.ups.com/adobe/assets/urn:aaid:aem:c271a615-2207-4626-8507-521abe266d37/original/as/uta-en-GLOBAL.pdf">UPS Technology Agreement</a>',
    maxLength: null,
    minLength: null,
    placeholderText: null,
    rank: 125,
  },
];

const upsConnectParameters: CarrierFormField[] = [
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'full_name',
    label: 'Full Name',
    maxLength: 20,
    minLength: null,
    placeholderText: null,
    rank: 10,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'title',
    label: 'Title',
    maxLength: 20,
    minLength: null,
    placeholderText: 'Your title (e.g. Doctor, Artist, Manager, ...)',
    rank: 15,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'company',
    label: 'Company',
    maxLength: 30,
    minLength: null,
    placeholderText: "If you don't have a company please fill with your full name",
    rank: 20,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'email',
    label: 'Email',
    maxLength: 50,
    minLength: null,
    placeholderText: null,
    rank: 25,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'phone',
    label: 'Phone',
    maxLength: 30,
    minLength: null,
    placeholderText: null,
    rank: 30,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'billing_address_street1',
    label: 'Street (1)',
    maxLength: 30,
    minLength: null,
    placeholderText: null,
    rank: 35,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: false,
    key: 'billing_address_street2',
    label: 'Street (2)',
    maxLength: 50,
    minLength: null,
    placeholderText: 'Apartment, suit, unit, floor, etc.',
    rank: 40,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'billing_address_city',
    label: 'City',
    maxLength: 30,
    minLength: null,
    placeholderText: null,
    rank: 50,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: false,
    key: 'billing_address_state',
    label: 'State (required for USA and Canada)',
    maxLength: 20,
    minLength: null,
    placeholderText: 'Use official code for other countries if applicable',
    rank: 55,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'billing_address_zip',
    label: 'Zip / Postal Code',
    maxLength: 10,
    minLength: null,
    placeholderText: null,
    rank: 60,
  },
  {
    displayType: 'show',
    fieldType: 'choice',
    helpUrl: null,
    isRequired: true,
    key: 'billing_address_country_iso2',
    label: 'Country',
    maxLength: null,
    minLength: null,
    options: UPS_REGISTRATION_COUNTRIES,
    placeholderText: null,
    rank: 65,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'account_number',
    label: 'UPS Account Number',
    maxLength: 20,
    minLength: null,
    placeholderText: null,
    rank: 70,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'collec_zip',
    label: 'Postcode (collection location)',
    maxLength: 10,
    minLength: null,
    placeholderText: null,
    rank: 80,
  },
  {
    displayType: 'show',
    fieldType: 'choice',
    helpUrl: null,
    isRequired: true,
    key: 'collec_country_iso2',
    label: 'Country',
    maxLength: null,
    minLength: null,
    options: UPS_REGISTRATION_COUNTRIES,
    placeholderText: null,
    rank: 85,
  },
  {
    defaultValue: false,
    displayType: 'show',
    fieldType: 'boolean',
    helpUrl: null,
    isRequired: false,
    key: 'has_invoice',
    label:
      '<b>Show personalized rates (requires latest invoice).</b><br />If disabled, you\'ll only see retail rates. <a style="color: #226015" href="https://support.goshippo.com/hc/en-us/articles/204781715-How-to-Connect-Your-UPS-Account-in-Shippo#h_877e2e40-64ad-4051-95fc-ea3b5afe6a4a">Learn more</a>',
    maxLength: null,
    minLength: null,
    placeholderText: null,
    rank: 90,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    hiddenByKey: 'has_invoice',
    hiddenByValue: false,
    isRequired: true,
    key: 'invoice_number',
    label: 'Invoice Number',
    maxLength: 30,
    minLength: null,
    placeholderText: 'Invoice number of a Regular account',
    rank: 95,
  },
  {
    displayType: 'show',
    fieldType: 'choice',
    helpUrl: null,
    hiddenByKey: 'has_invoice',
    hiddenByValue: false,
    isRequired: true,
    key: 'currency_code',
    label: 'Currency Code',
    maxLength: null,
    minLength: null,
    options: [
      {
        label: 'AED - United Arab Emirates Dirham',
        value: 'AED',
      },
      {
        label: 'AFN - Afghan Afghani',
        value: 'AFN',
      },
      {
        label: 'ALL - Albanian Lek',
        value: 'ALL',
      },
      {
        label: 'AMD - Armenian Dram',
        value: 'AMD',
      },
      {
        label: 'ANG - Netherlands Antillean Guilder',
        value: 'ANG',
      },
      {
        label: 'AOA - Angolan Kwanza',
        value: 'AOA',
      },
      {
        label: 'ARS - Argentine Peso',
        value: 'ARS',
      },
      {
        label: 'AUD - Australian Dollar',
        value: 'AUD',
      },
      {
        label: 'AWG - Aruban Florin',
        value: 'AWG',
      },
      {
        label: 'AZN - Azerbaijani Manat',
        value: 'AZN',
      },
      {
        label: 'BAM - Bosnia-Herzegovina Convertible Mark',
        value: 'BAM',
      },
      {
        label: 'BBD - Barbadian Dollar',
        value: 'BBD',
      },
      {
        label: 'BDT - Bangladeshi Taka',
        value: 'BDT',
      },
      {
        label: 'BGN - Bulgarian Lev',
        value: 'BGN',
      },
      {
        label: 'BHD - Bahraini Dinar',
        value: 'BHD',
      },
      {
        label: 'BIF - Burundian Franc',
        value: 'BIF',
      },
      {
        label: 'BMD - Bermudan Dollar',
        value: 'BMD',
      },
      {
        label: 'BND - Brunei Dollar',
        value: 'BND',
      },
      {
        label: 'BOB - Bolivian Boliviano',
        value: 'BOB',
      },
      {
        label: 'BRL - Brazilian Real',
        value: 'BRL',
      },
      {
        label: 'BSD - Bahamian Dollar',
        value: 'BSD',
      },
      {
        label: 'BTN - Bhutanese Ngultrum',
        value: 'BTN',
      },
      {
        label: 'BWP - Botswanan Pula',
        value: 'BWP',
      },
      {
        label: 'BYR - Belarusian Ruble',
        value: 'BYR',
      },
      {
        label: 'BZD - Belize Dollar',
        value: 'BZD',
      },
      {
        label: 'CAD - Canadian Dollar',
        value: 'CAD',
      },
      {
        label: 'CDF - Congolese Franc',
        value: 'CDF',
      },
      {
        label: 'CHF - Swiss Franc',
        value: 'CHF',
      },
      {
        label: 'CLF - Chilean Unit of Account (UF)',
        value: 'CLF',
      },
      {
        label: 'CLP - Chilean Peso',
        value: 'CLP',
      },
      {
        label: 'COP - Colombian Peso',
        value: 'COP',
      },
      {
        label: 'CRC - Costa Rican Colón',
        value: 'CRC',
      },
      {
        label: 'CUP - Cuban Peso',
        value: 'CUP',
      },
      {
        label: 'CVE - Cape Verdean Escudo',
        value: 'CVE',
      },
      {
        label: 'CZK - Czech Republic Koruna',
        value: 'CZK',
      },
      {
        label: 'DJF - Djiboutian Franc',
        value: 'DJF',
      },
      {
        label: 'DKK - Danish Krone',
        value: 'DKK',
      },
      {
        label: 'DOP - Dominican Peso',
        value: 'DOP',
      },
      {
        label: 'DZD - Algerian Dinar',
        value: 'DZD',
      },
      {
        label: 'EGP - Egyptian Pound',
        value: 'EGP',
      },
      {
        label: 'ERN - Eritrean Nakfa',
        value: 'ERN',
      },
      {
        label: 'ETB - Ethiopian Birr',
        value: 'ETB',
      },
      {
        label: 'EUR - Euro',
        value: 'EUR',
      },
      {
        label: 'FJD - Fijian Dollar',
        value: 'FJD',
      },
      {
        label: 'FKP - Falkland Islands Pound',
        value: 'FKP',
      },
      {
        label: 'GBP - British Pound Sterling',
        value: 'GBP',
      },
      {
        label: 'GEL - Georgian Lari',
        value: 'GEL',
      },
      {
        label: 'GHS - Ghanaian Cedi',
        value: 'GHS',
      },
      {
        label: 'GIP - Gibraltar Pound',
        value: 'GIP',
      },
      {
        label: 'GMD - Gambian Dalasi',
        value: 'GMD',
      },
      {
        label: 'GNF - Guinean Franc',
        value: 'GNF',
      },
      {
        label: 'GTQ - Guatemalan Quetzal',
        value: 'GTQ',
      },
      {
        label: 'GYD - Guyanaese Dollar',
        value: 'GYD',
      },
      {
        label: 'HKD - Hong Kong Dollar',
        value: 'HKD',
      },
      {
        label: 'HNL - Honduran Lempira',
        value: 'HNL',
      },
      {
        label: 'HRK - Croatian Kuna',
        value: 'HRK',
      },
      {
        label: 'HTG - Haitian Gourde',
        value: 'HTG',
      },
      {
        label: 'HUF - Hungarian Forint',
        value: 'HUF',
      },
      {
        label: 'IDR - Indonesian Rupiah',
        value: 'IDR',
      },
      {
        label: 'ILS - Israeli New Sheqel',
        value: 'ILS',
      },
      {
        label: 'INR - Indian Rupee',
        value: 'INR',
      },
      {
        label: 'IQD - Iraqi Dinar',
        value: 'IQD',
      },
      {
        label: 'IRR - Iranian Rial',
        value: 'IRR',
      },
      {
        label: 'ISK - Icelandic Króna',
        value: 'ISK',
      },
      {
        label: 'JMD - Jamaican Dollar',
        value: 'JMD',
      },
      {
        label: 'JOD - Jordanian Dinar',
        value: 'JOD',
      },
      {
        label: 'JPY - Japanese Yen',
        value: 'JPY',
      },
      {
        label: 'KES - Kenyan Shilling',
        value: 'KES',
      },
      {
        label: 'KGS - Kyrgystani Som',
        value: 'KGS',
      },
      {
        label: 'KHR - Cambodian Riel',
        value: 'KHR',
      },
      {
        label: 'KMF - Comorian Franc',
        value: 'KMF',
      },
      {
        label: 'KPW - North Korean Won',
        value: 'KPW',
      },
      {
        label: 'KRW - South Korean Won',
        value: 'KRW',
      },
      {
        label: 'KWD - Kuwaiti Dinar',
        value: 'KWD',
      },
      {
        label: 'KYD - Cayman Islands Dollar',
        value: 'KYD',
      },
      {
        label: 'KZT - Kazakhstani Tenge',
        value: 'KZT',
      },
      {
        label: 'LAK - Laotian Kip',
        value: 'LAK',
      },
      {
        label: 'LBP - Lebanese Pound',
        value: 'LBP',
      },
      {
        label: 'LKR - Sri Lankan Rupee',
        value: 'LKR',
      },
      {
        label: 'LRD - Liberian Dollar',
        value: 'LRD',
      },
      {
        label: 'LSL - Lesotho Loti',
        value: 'LSL',
      },
      {
        label: 'LYD - Libyan Dinar',
        value: 'LYD',
      },
      {
        label: 'MAD - Moroccan Dirham',
        value: 'MAD',
      },
      {
        label: 'MDL - Moldovan Leu',
        value: 'MDL',
      },
      {
        label: 'MGA - Malagasy Ariary',
        value: 'MGA',
      },
      {
        label: 'MKD - Macedonian Denar',
        value: 'MKD',
      },
      {
        label: 'MMK - Myanma Kyat',
        value: 'MMK',
      },
      {
        label: 'MNT - Mongolian Tugrik',
        value: 'MNT',
      },
      {
        label: 'MOP - Macanese Pataca',
        value: 'MOP',
      },
      {
        label: 'MRO - Mauritanian Ouguiya',
        value: 'MRO',
      },
      {
        label: 'MUR - Mauritian Rupee',
        value: 'MUR',
      },
      {
        label: 'MVR - Maldivian Rufiyaa',
        value: 'MVR',
      },
      {
        label: 'MWK - Malawian Kwacha',
        value: 'MWK',
      },
      {
        label: 'MXN - Mexican Peso',
        value: 'MXN',
      },
      {
        label: 'MYR - Malaysian Ringgit',
        value: 'MYR',
      },
      {
        label: 'MZN - Mozambican Metical',
        value: 'MZN',
      },
      {
        label: 'NAD - Namibian Dollar',
        value: 'NAD',
      },
      {
        label: 'NGN - Nigerian Naira',
        value: 'NGN',
      },
      {
        label: 'NIO - Nicaraguan Córdoba',
        value: 'NIO',
      },
      {
        label: 'NOK - Norwegian Krone',
        value: 'NOK',
      },
      {
        label: 'NPR - Nepalese Rupee',
        value: 'NPR',
      },
      {
        label: 'NZD - New Zealand Dollar',
        value: 'NZD',
      },
      {
        label: 'OMR - Omani Rial',
        value: 'OMR',
      },
      {
        label: 'PAB - Panamanian Balboa',
        value: 'PAB',
      },
      {
        label: 'PEN - Peruvian Nuevo Sol',
        value: 'PEN',
      },
      {
        label: 'PGK - Papua New Guinean Kina',
        value: 'PGK',
      },
      {
        label: 'PHP - Philippine Peso',
        value: 'PHP',
      },
      {
        label: 'PKR - Pakistani Rupee',
        value: 'PKR',
      },
      {
        label: 'PLN - Polish Zloty',
        value: 'PLN',
      },
      {
        label: 'PYG - Paraguayan Guarani',
        value: 'PYG',
      },
      {
        label: 'QAR - Qatari Rial',
        value: 'QAR',
      },
      {
        label: 'RMB - Chinese Yuan Renminbi',
        value: 'RMB',
      },
      {
        label: 'RON - Romanian Leu',
        value: 'RON',
      },
      {
        label: 'RSD - Serbian Dinar',
        value: 'RSD',
      },
      {
        label: 'RUB - Russian Ruble',
        value: 'RUB',
      },
      {
        label: 'RWF - Rwandan Franc',
        value: 'RWF',
      },
      {
        label: 'SAR - Saudi Riyal',
        value: 'SAR',
      },
      {
        label: 'SBD - Solomon Islands Dollar',
        value: 'SBD',
      },
      {
        label: 'SCR - Seychellois Rupee',
        value: 'SCR',
      },
      {
        label: 'SEK - Swedish Krona',
        value: 'SEK',
      },
      {
        label: 'SGD - Singapore Dollar',
        value: 'SGD',
      },
      {
        label: 'SHP - Saint Helena Pound',
        value: 'SHP',
      },
      {
        label: 'SLL - Sierra Leonean Leone',
        value: 'SLL',
      },
      {
        label: 'SOS - Somali Shilling',
        value: 'SOS',
      },
      {
        label: 'SRD - Surinamese Dollar',
        value: 'SRD',
      },
      {
        label: 'STD - São Tomé and Príncipe Dobra',
        value: 'STD',
      },
      {
        label: 'SVC - Salvadoran Colón',
        value: 'SVC',
      },
      {
        label: 'SYP - Syrian Pound',
        value: 'SYP',
      },
      {
        label: 'SZL - Swazi Lilangeni',
        value: 'SZL',
      },
      {
        label: 'THB - Thai Baht',
        value: 'THB',
      },
      {
        label: 'TJS - Tajikistani Somoni',
        value: 'TJS',
      },
      {
        label: 'TND - Tunisian Dinar',
        value: 'TND',
      },
      {
        label: 'TOP - Tongan Pa´anga',
        value: 'TOP',
      },
      {
        label: 'TRY - Turkish Lira',
        value: 'TRY',
      },
      {
        label: 'TTD - Trinidad and Tobago Dollar',
        value: 'TTD',
      },
      {
        label: 'TWD - New Taiwan Dollar',
        value: 'TWD',
      },
      {
        label: 'TZS - Tanzanian Shilling',
        value: 'TZS',
      },
      {
        label: 'UAH - Ukrainian Hryvnia',
        value: 'UAH',
      },
      {
        label: 'UGX - Ugandan Shilling',
        value: 'UGX',
      },
      {
        label: 'USD - United States Dollar',
        value: 'USD',
      },
      {
        label: 'UYU - Uruguayan Peso',
        value: 'UYU',
      },
      {
        label: 'UZS - Uzbekistan Som',
        value: 'UZS',
      },
      {
        label: 'VND - Vietnamese Dong',
        value: 'VND',
      },
      {
        label: 'VUV - Vanuatu Vatu',
        value: 'VUV',
      },
      {
        label: 'WST - Samoan Tala',
        value: 'WST',
      },
      {
        label: 'XAF - CFA Franc BEAC',
        value: 'XAF',
      },
      {
        label: 'XCD - East Caribbean Dollar',
        value: 'XCD',
      },
      {
        label: 'XOF - CFA Franc BCEAO',
        value: 'XOF',
      },
      {
        label: 'XPF - CFP Franc',
        value: 'XPF',
      },
      {
        label: 'YER - Yemeni Rial',
        value: 'YER',
      },
      {
        label: 'ZAR - South African Rand',
        value: 'ZAR',
      },
      {
        label: 'ZMK - Zambian Kwacha (pre-2013)',
        value: 'ZMK',
      },
    ],
    placeholderText: null,
    rank: 100,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    hiddenByKey: 'has_invoice',
    hiddenByValue: false,
    isRequired: true,
    key: 'invoice_value',
    label: 'Charges This Period',
    maxLength: 30,
    minLength: null,
    placeholderText: null,
    rank: 105,
    tooltip: 'Value of the invoice. Max of 16 digits before decimal and 2 digits after decimal.',
  },
  {
    displayType: 'show',
    fieldType: 'choice',
    helpUrl: null,
    hiddenByKey: 'has_invoice',
    hiddenByValue: false,
    isRequired: true,
    key: 'aia_country_iso2',
    label: 'Country',
    maxLength: 30,
    minLength: null,
    options: UPS_REGISTRATION_COUNTRIES,
    placeholderText: 'Country associated with the account that issued the invoice',
    rank: 110,
  },
  {
    displayType: 'show',
    fieldType: 'date',
    helpUrl: null,
    hiddenByKey: 'has_invoice',
    hiddenByValue: false,
    isRequired: true,
    key: 'invoice_date',
    label: 'Invoice Date',
    maxLength: null,
    minLength: null,
    placeholderText: null,
    rank: 115,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    hiddenByKey: 'has_invoice',
    hiddenByValue: false,
    isRequired: false,
    key: 'invoice_controlid',
    label: 'Control ID (required for USA)',
    maxLength: 30,
    minLength: null,
    placeholderText: null,
    rank: 120,
  },
  {
    displayType: 'show',
    fieldType: 'boolean',
    helpUrl: null,
    isRequired: true,
    key: 'ups_agreements',
    label:
      'By checking this box, you agree to the <a style="color: #226015" rel="noreferrer noopener" target="_blank" href="https://assets.ups.com/adobe/assets/urn:aaid:aem:c271a615-2207-4626-8507-521abe266d37/original/as/uta-en-GLOBAL.pdf">UPS Technology Agreement</a>',
    maxLength: null,
    minLength: null,
    placeholderText: null,
    rank: 125,
  },
];

const amazonShippingParameters: CarrierFormField[] = [
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'full_name',
    label: 'Full Name',
    maxLength: 100,
    minLength: null,
    placeholderText: null,
    rank: 10,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'company',
    label: 'Company',
    maxLength: 100,
    minLength: null,
    placeholderText: null,
    rank: 20,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'address',
    label: 'Address',
    maxLength: 200,
    minLength: null,
    placeholderText: null,
    rank: 30,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'city',
    label: 'City',
    maxLength: 100,
    minLength: null,
    placeholderText: null,
    rank: 40,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'state',
    label: 'State',
    maxLength: 50,
    minLength: null,
    placeholderText: null,
    rank: 50,
  },
  {
    displayType: 'show',
    fieldType: 'choice',
    helpUrl: null,
    isRequired: true,
    key: 'country',
    label: 'Country',
    maxLength: null,
    minLength: null,
    options: [
      {
        label: 'United States',
        value: 'US',
      },
    ],
    placeholderText: null,
    rank: 60,
  },
  {
    displayType: 'show',
    fieldType: 'string',
    helpUrl: null,
    isRequired: true,
    key: 'zip',
    label: 'ZIP Code',
    maxLength: 10,
    minLength: null,
    placeholderText: null,
    rank: 70,
  },
  {
    displayType: 'show',
    fieldType: 'boolean',
    helpUrl: null,
    isRequired: true,
    key: 'amazon_shipping_agreements',
    label:
      'By checking this box, you agree to <a style="color: #226015" rel="noreferrer noopener" target="_blank" href="https://shipping.amazon.com/site-terms">Amazon Shipping Conditions of Use</a>',
    maxLength: null,
    minLength: null,
    placeholderText: null,
    rank: 125,
  },
  {
    defaultValue: true,
    displayType: 'show',
    fieldType: 'boolean',
    helpUrl: null,
    hiddenOnProd: true,
    isRequired: false,
    key: 'is_test',
    label: 'Test Mode',
    maxLength: null,
    minLength: null,
    placeholderText: null,
    rank: 130,
  },
];

// Helper function to filter out development-only parameters in production
export const filterParametersForEnvironment = (
  parameters: CarrierFormField[],
  envId: string | undefined = typeof ENV_ID !== 'undefined' ? ENV_ID : undefined,
): CarrierFormField[] =>
  envId === 'production' ? parameters.filter((param) => !param.hiddenOnProd) : parameters;

type ParameterMap = {
  [carrier: string]: { [key in CarrierFormType]: Maybe<CarrierFormField[]> };
};

export const parametersMap: ParameterMap = {
  amazon_shipping: {
    connect: null,
    create: filterParametersForEnvironment(amazonShippingParameters),
    edit: null,
  },
  canada_post: {
    connect: canadaPostParameters,
    create: canadaPostCreateParameters,
    edit: canadaPostParameters,
  },
  dhl_express: {
    connect: dhlExpressParameters,
    create: dhlExpressParameters,
    edit: dhlExpressParameters,
  },
  lasership: {
    connect: lasershipParameters,
    create: lasershipParameters,
    edit: lasershipParameters,
  },
  ups: {
    connect: upsConnectParameters,
    create: null, // The UPS Master create form is DB-backed, rest are mocked
    edit: upsEditParameters,
  },
  ups_oauth: {
    connect: upsConnectParametersOauth,
    create: null,
    edit: upsEditParameters,
  },
};

export const isCarrierFormMocked = (carrier: string, formType: CarrierFormType) =>
  !!(parametersMap.hasOwnProperty(carrier) && parametersMap[carrier][formType]);
